import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Search,
  Download,
  Eye,
  Calendar,
  Info,
  ArrowRight,
  Ship,
  Package,
  Truck,
  Tag,
  Zap,
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDirection } from '@/contexts/DirectionContext';
import {
  OFFER_STATUSES,
  fmtDate,
  daysFromNow,
  statusLabel,
} from '@/features/dashboard/data/claudeMockData';
import { CompanyBadge } from '@/features/dashboard/components/claude/shared';
import { useOfferCards } from '../hooks/useOfferCards';
import type {
  OfferCard,
  OfferCardFreightRow,
  OfferCardLocaleRow,
  OfferCardAutoRow,
  OfferCardOtherRow,
} from '../interfaces/offer-card.interface';
import { OfferCardModal } from '../components/OfferCardModal';
import { buildCompanyForBadge } from '../utils/buildCompanyForBadge';
import { exportXlsx, type ExcelColumn } from '@/utils/exportXlsx';

type TabId = 'all' | 'valid' | 'expired';
type SortKey = 'issued' | 'validUntil' | 'price';

const OffersIndex: React.FC = () => {
  const { currentLanguage: lang } = useLanguage();
  const { direction } = useDirection();
  const isRo = lang === 'ro';
  const isExport = direction === 'export';

  const { data, isLoading } = useOfferCards();
  const offers: OfferCard[] = data || [];

  const [tab, setTab] = useState<TabId>('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('issued');
  const [active, setActive] = useState<OfferCard | null>(null);

  // Background claude-design cât e montată pagina.
  useEffect(() => {
    document.body.classList.add('dashboard-claude-bg');
    return () => {
      document.body.classList.remove('dashboard-claude-bg');
    };
  }, []);

  // Deep-link `?focus=<id>` din command palette → deschide modal-ul ofertei.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const focus = searchParams.get('focus');
    if (!focus || offers.length === 0) return;
    const id = Number(focus);
    if (!Number.isFinite(id)) return;
    const target = offers.find((o) => o.id === id);
    if (target) {
      setActive(target);
      const next = new URLSearchParams(searchParams);
      next.delete('focus');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, offers]);

  const counts = useMemo(
    () => ({
      all: offers.length,
      valid: offers.filter((o) => o.status === 'valid').length,
      expired: offers.filter((o) => o.status === 'expired').length,
    }),
    [offers],
  );

  const filtered = useMemo(() => {
    let arr = offers;
    if (tab !== 'all') {
      arr = arr.filter((o) => o.status === tab);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      arr = arr.filter((o) => {
        if (o.ref.toLowerCase().includes(q)) return true;
        if (o.companyName.toLowerCase().includes(q)) return true;
        if (o.containerTypes.some((t) => t.toLowerCase().includes(q))) return true;
        if (
          o.freightRows.some(
            (r) =>
              (r.pol || '').toLowerCase().includes(q) ||
              (r.pod || '').toLowerCase().includes(q) ||
              (r.polName || '').toLowerCase().includes(q) ||
              (r.podName || '').toLowerCase().includes(q),
          )
        )
          return true;
        if (o.localeRows.some((r) => (r.name || '').toLowerCase().includes(q))) return true;
        if (o.autoRows.some((r) => (r.name || '').toLowerCase().includes(q))) return true;
        return false;
      });
    }

    return [...arr].sort((a, b) => {
      if (sort === 'issued') {
        const ta = a.issuedAt ? new Date(a.issuedAt).getTime() : 0;
        const tb = b.issuedAt ? new Date(b.issuedAt).getTime() : 0;
        return tb - ta;
      }
      if (sort === 'validUntil') {
        const ta = a.validityEndDate ? new Date(a.validityEndDate).getTime() : Infinity;
        const tb = b.validityEndDate ? new Date(b.validityEndDate).getTime() : Infinity;
        return ta - tb;
      }
      if (sort === 'price') {
        const minPrice = (o: OfferCard): number => {
          const vals: number[] = [];
          for (const r of o.freightRows) for (const p of r.prices) if (p != null) vals.push(p);
          return vals.length ? Math.min(...vals) : Number.POSITIVE_INFINITY;
        };
        return minPrice(a) - minPrice(b);
      }
      return 0;
    });
  }, [offers, tab, search, sort]);

  const TABS: Array<{ id: TabId; labelRo: string; labelEn: string }> = [
    { id: 'all', labelRo: 'Toate', labelEn: 'All' },
    { id: 'valid', labelRo: 'Valabile', labelEn: 'Valid' },
    { id: 'expired', labelRo: 'Expirate', labelEn: 'Expired' },
  ];

  // Export Excel — aceleași coloane ca matricea din carduri
  // (Prestație/Serviciu + Terminal pe export + un preț per tip de container).
  const handleExport = () => {
    if (filtered.length === 0) return;
    const today = new Date().toISOString().slice(0, 10);

    const typeCols: string[] = [];
    for (const o of filtered) {
      for (const t of o.containerTypes || []) {
        if (t && !typeCols.includes(t)) typeCols.push(t);
      }
    }

    interface ExportRow {
      ref: string;
      status: string;
      company: string;
      validFrom: Date | null;
      validUntil: Date | null;
      service: string;
      terminal: string;
      prices: Record<string, string>;
    }

    const rows: ExportRow[] = [];
    for (const o of filtered) {
      const offerIsExport =
        isExport || o.direction === 'export' || o.type?.toLowerCase() === 'export';
      const types = o.containerTypes || [];
      const base = {
        ref: o.ref,
        status: statusLabel(OFFER_STATUSES, o.status, lang) || o.status,
        company: o.companyName || '',
        validFrom: o.validityStartDate ? new Date(o.validityStartDate) : null,
        validUntil: o.validityEndDate ? new Date(o.validityEndDate) : null,
      };

      const priceMap = (
        prices: Array<number | null | undefined>,
        currency: string | null | undefined,
        plusVat: boolean,
      ): Record<string, string> => {
        const map: Record<string, string> = {};
        types.forEach((ct, idx) => {
          map[ct] = offerIsExport
            ? formatExportPrice(prices[idx], currency || o.currency, plusVat || !!o.navluVat, lang)
            : formatImportPrice(prices[idx] ?? null, currency || o.currency, plusVat, lang);
        });
        return map;
      };

      for (const f of o.freightRows) {
        rows.push({
          ...base,
          service: freightServiceLabel(f),
          terminal: offerIsExport ? o.terminal || '' : '',
          prices: priceMap(f.prices, f.currency, !!f.plusVat),
        });
      }

      if (!offerIsExport) {
        for (const l of o.localeRows) {
          rows.push({
            ...base,
            service: l.name || '',
            terminal: '',
            prices: priceMap(l.prices, l.currency, !!l.plusVat),
          });
        }
        for (const a of o.autoRows) {
          const map: Record<string, string> = {};
          if (types[0]) {
            map[types[0]] = formatImportPrice(a.price ?? null, a.currency, !!a.plusVat, lang);
          }
          rows.push({
            ...base,
            service: a.name || '',
            terminal: '',
            prices: map,
          });
        }
        for (const x of o.otherRows) {
          rows.push({
            ...base,
            service: x.name || '',
            terminal: '',
            prices: priceMap(x.prices, x.currency, !!x.plusVat),
          });
        }
      }
    }

    const columns: ExcelColumn<ExportRow>[] = [
      { header: isRo ? 'Referință' : 'Reference', value: (r) => r.ref, width: 16 },
      { header: 'Status', value: (r) => r.status, width: 12 },
      { header: isRo ? 'Firmă' : 'Company', value: (r) => r.company, width: 26 },
      { header: isRo ? 'Valid de la' : 'Valid from', value: (r) => r.validFrom, width: 12 },
      { header: isRo ? 'Valid până la' : 'Valid until', value: (r) => r.validUntil, width: 12 },
      {
        header: isExport ? (isRo ? 'Prestație' : 'Service') : isRo ? 'Serviciu' : 'Service',
        value: (r) => r.service,
        width: 38,
      },
      ...(isExport
        ? [{ header: 'Terminal', value: (r: ExportRow) => r.terminal, width: 14 }]
        : []),
      ...typeCols.map((ct) => ({
        header: ct,
        value: (r: ExportRow) => r.prices[ct] || '',
        width: 14,
      })),
    ];

    exportXlsx<ExportRow>({
      fileName: `oferte-${today}`,
      sheetName: isRo ? 'Oferte' : 'Offers',
      rows,
      columns,
    });
  };

  return (
    <div style={{ color: 'var(--text)' }}>
      <div
        style={{
          marginBottom: 18,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', margin: 0 }}>
            {isRo ? 'Oferte' : 'Offers'}
          </h1>
          <div className="muted fs-13 mt-1">
            {isExport
              ? isRo
                ? 'Tarife export: navlu (FOT/FO), taxe locale și transport terestru.'
                : 'Export rates: ocean freight (FOT/FO), local charges and inland transport.'
              : isRo
                ? 'Tarifele transmise de TLS (navlu, taxe locale, transport).'
                : 'Rates issued by TLS (freight, local charges, inland).'}
          </div>
        </div>
        <button type="button" className="btn btn-primary btn-sm">
          <Zap size={13} />
          {isRo ? 'Cere ofertă nouă' : 'Request new quote'}
        </button>
      </div>

      <div
        style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--border)' }}
      >
        {TABS.map((tb) => (
          <button
            key={tb.id}
            type="button"
            className="modal-tab"
            style={{
              marginBottom: -1,
              borderBottom: '2px solid ' + (tab === tb.id ? 'var(--accent)' : 'transparent'),
              color: tab === tb.id ? 'var(--text)' : 'var(--text-muted)',
            }}
            onClick={() => setTab(tb.id)}
          >
            {isRo ? tb.labelRo : tb.labelEn}
            <span className="subtle" style={{ marginLeft: 6, fontSize: 11 }}>
              {counts[tb.id]}
            </span>
          </button>
        ))}
      </div>

      <div className="toolbar">
        <div className="search-input">
          <Search size={14} />
          <input
            placeholder={isRo ? 'Referință, rută, tip, preț…' : 'Reference, route, type, price…'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          style={{
            background: 'var(--surface)',
            color: 'var(--text)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)',
            padding: '6px 10px',
            fontSize: 12.5,
            cursor: 'pointer',
          }}
        >
          <option value="issued">{isRo ? 'Sortare: după emitere' : 'Sort: by issued date'}</option>
          <option value="validUntil">{isRo ? 'Sortare: după expirare' : 'Sort: by expiry'}</option>
          <option value="price">
            {isRo ? 'Sortare: după preț min.' : 'Sort: by min. price'}
          </option>
        </select>
        <div className="toolbar-spacer" />
        <button
          type="button"
          className="btn btn-sm"
          onClick={handleExport}
          disabled={filtered.length === 0}
          title={
            filtered.length === 0
              ? isRo
                ? 'Nimic de exportat'
                : 'Nothing to export'
              : undefined
          }
        >
          <Download size={13} />
          {isRo ? 'Export' : 'Export'}
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="card">
          <div className="empty">
            <div className="icon">
              <Tag size={22} strokeWidth={2} />
            </div>
            <div className="fw-600">
              {isLoading
                ? isRo
                  ? 'Se încarcă…'
                  : 'Loading…'
                : isRo
                ? 'Nicio ofertă'
                : 'No offers'}
            </div>
            {!isLoading && (
              <div className="muted fs-12 mt-1">
                {isRo
                  ? 'Schimbați filtrele sau căutarea.'
                  : 'Try changing filters or search.'}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="offer-cards">
          {filtered.map((o) => (
            <OfferCardView
              key={o.id}
              offer={o}
              lang={lang}
              isExport={isExport || o.direction === 'export' || o.type?.toLowerCase() === 'export'}
              onView={() => setActive(o)}
            />
          ))}
        </div>
      )}

      <OfferCardModal isOpen={active !== null} offer={active} onClose={() => setActive(null)} />
    </div>
  );
};

export default OffersIndex;

// ============================================================
// Card individual de ofertă
// ============================================================

/** Label pentru linia de navlu: pe export preferă `name` (FOT/FO); pe import POL→POD. */
function freightServiceLabel(r: OfferCardFreightRow): string {
  const name = (r.name || '').trim();
  if (name) return name;
  const pol = r.polName || r.pol;
  const pod = r.podName || r.pod;
  if (pol && pod) return `${pol} → ${pod}`;
  if (pol) return pol;
  if (pod) return pod;
  return '—';
}

const OfferCardView: React.FC<{
  offer: OfferCard;
  lang: 'ro' | 'en';
  isExport: boolean;
  onView: () => void;
}> = ({ offer, lang, isExport, onView }) => {
  const isRo = lang === 'ro';
  const st = OFFER_STATUSES[offer.status];
  const co = buildCompanyForBadge(offer.companyName, offer.companyId);
  const isExpired = offer.status === 'expired';
  const dLeft = offer.validityEndDate ? daysFromNow(offer.validityEndDate) : null;
  const types = offer.containerTypes || [];

  const hasFreight = offer.freightRows.length > 0;
  // Export preview (createTableExport) = DOAR navlu all-in: text + terminal + prețuri.
  // Locale/auto nu apar pe export.
  const hasLocale = !isExport && offer.localeRows.length > 0;
  const hasAuto = !isExport && offer.autoRows.length > 0;
  const hasOther = !isExport && offer.otherRows.length > 0;
  const exportColSpan = types.length + 2; // Prestație + Terminal + tipuri
  const importColSpan = types.length + 1;

  return (
    <div className={'offer-card' + (isExpired ? ' offer-card-expired' : '')}>
      <div className="offer-card-head">
        <div className="offer-head-main">
          <div className="offer-head-row">
            <span className="offer-ref mono">{offer.ref}</span>
            {st && (
              <span className={'badge ' + st.badge}>
                <span className="dot" />
                {statusLabel(OFFER_STATUSES, offer.status, lang)}
              </span>
            )}
            <CompanyBadge company={co} />
          </div>
          <div className="offer-head-meta">
            <span className="offer-validity">
              <Calendar size={12} />
              <span className="muted">{isRo ? 'Valabilitate' : 'Validity'}:</span>
              <span className="mono fw-500">
                {fmtDate(offer.validityStartDate, lang)} → {fmtDate(offer.validityEndDate, lang)}
              </span>
              {!isExpired && dLeft != null && dLeft >= 0 && dLeft <= 5 && (
                <span className="offer-expires-soon">
                  · {isRo ? `expiră în ${dLeft}${dLeft === 1 ? ' zi' : ' zile'}` : `expires in ${dLeft}d`}
                </span>
              )}
            </span>
          </div>
        </div>
        <button
          type="button"
          className="btn btn-sm offer-view-btn"
          onClick={onView}
          title={isRo ? 'Vezi detalii' : 'View details'}
        >
          <Eye size={13} strokeWidth={2} />
          <span className="hidden-mobile">{isRo ? 'Vezi detalii' : 'View'}</span>
        </button>
      </div>

      {!isExport && (
        <div className="offer-notes">
          <Info size={12} />
          {isRo
            ? 'Deschide oferta din «Vezi detalii» pentru a analiza toți termenii și condițiile.'
            : 'Open the offer via «View details» to review all terms and conditions.'}
        </div>
      )}

      {/* Desktop matrix */}
      <div className="offer-matrix hidden-mobile">
        <table className="offer-table">
          <thead>
            <tr>
              <th className="offer-th-service">
                {isExport ? (isRo ? 'Prestație' : 'Service') : isRo ? 'Serviciu' : 'Service'}
              </th>
              {isExport ? <th className="offer-th-terminal">Terminal</th> : null}
              {types.map((tt) => (
                <th key={tt} className="offer-th-type">
                  {tt}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {hasFreight && (
              <FreightSection
                rows={offer.freightRows}
                types={types}
                lang={lang}
                isExport={isExport}
                terminal={offer.terminal}
                currencyCode={offer.currency}
                currencySymbol={offer.currencySymbol}
                navluVat={!!offer.navluVat}
              />
            )}
            {hasLocale && (
              <LocaleSection rows={offer.localeRows} types={types} lang={lang} isExport={false} />
            )}
            {hasAuto && (
              <AutoSection rows={offer.autoRows} types={types} lang={lang} isExport={false} />
            )}
            {hasOther && (
              <OtherSection rows={offer.otherRows} types={types} lang={lang} isExport={false} />
            )}
            {!hasFreight && !hasLocale && !hasAuto && !hasOther && (
              <tr>
                <td colSpan={isExport ? exportColSpan : importColSpan}>
                  <div className="muted fs-12" style={{ padding: '14px 18px', textAlign: 'center' }}>
                    {isRo ? 'Niciun element în ofertă.' : 'No elements in this offer.'}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile stacked */}
      <div className="offer-matrix-mobile show-mobile">
        {hasFreight && (
          <FreightMobile
            rows={offer.freightRows}
            types={types}
            lang={lang}
            isExport={isExport}
            terminal={offer.terminal}
            currencyCode={offer.currency}
            currencySymbol={offer.currencySymbol}
            navluVat={!!offer.navluVat}
          />
        )}
        {hasLocale && (
          <LocaleMobile rows={offer.localeRows} types={types} lang={lang} isExport={false} />
        )}
        {hasAuto && <AutoMobile rows={offer.autoRows} lang={lang} isExport={false} />}
        {hasOther && (
          <OtherMobile rows={offer.otherRows} types={types} lang={lang} isExport={false} />
        )}
      </div>
    </div>
  );
};

// ============================================================
// Section helpers
// ============================================================

const formatPrice = (price: number, symbol: string | null, code: string | null) => {
  const sym = symbol || (code === 'EUR' ? '€' : code === 'RON' ? '' : '$');
  const suffix = code === 'RON' ? ' RON' : '';
  return `${sym}${price.toLocaleString('en-US')}${suffix}`;
};

/** Format import ca în tabel: „€850 +TVA". */
const formatImportPrice = (
  price: number | null | undefined,
  code: string | null | undefined,
  plusVat: boolean,
  lang: 'ro' | 'en',
): string => {
  if (price == null) return '';
  const n = Number(price);
  if (!Number.isFinite(n)) return '';
  const symbol = code === 'EUR' ? '€' : code === 'RON' ? '' : code === 'USD' ? '$' : '';
  const suffix = code === 'RON' ? ' RON' : !symbol && code ? ` ${code}` : '';
  const vat = plusVat ? (lang === 'ro' ? ' +TVA' : ' +VAT') : '';
  return `${symbol}${n.toLocaleString('en-US')}${suffix}${vat}`;
};

/** Format ca în preview export: „850 USD + TVA" (fără 0). */
const formatExportPrice = (
  price: number | null | undefined,
  code: string | null | undefined,
  vat: boolean,
  lang: 'ro' | 'en',
) => {
  if (price == null) return '';
  const n = Number(price);
  if (!Number.isFinite(n) || n === 0) return '';
  const formatted = n % 1 === 0 ? n.toFixed(0) : n.toFixed(2);
  const c = code || '';
  return `${formatted}${c ? ` ${c}` : ''}${vat ? (lang === 'ro' ? ' + TVA' : ' + VAT') : ''}`;
};

const PriceCell: React.FC<{
  price: number | null;
  symbol: string | null;
  code: string | null;
  plusVat?: boolean;
  lang?: 'ro' | 'en';
  /** Pe export, null → „inclus" (all-in), nu „—". */
  emptyAsIncluded?: boolean;
}> = ({ price, symbol, code, plusVat, lang = 'ro', emptyAsIncluded }) => {
  if (price == null) {
    if (emptyAsIncluded) {
      return (
        <span className="muted fs-12" style={{ fontStyle: 'italic' }}>
          {lang === 'ro' ? 'inclus' : 'incl.'}
        </span>
      );
    }
    return <span className="offer-cell-empty">—</span>;
  }
  return (
    <span
      className="mono fw-600 fs-13"
      style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'baseline', gap: 4 }}
    >
      {formatPrice(price, symbol, code)}
      {plusVat && (
        <span className="offer-vat" style={{ marginLeft: 2 }}>
          {lang === 'ro' ? '+TVA' : '+VAT'}
        </span>
      )}
    </span>
  );
};

type SectionKind = 'freight' | 'thc' | 'land' | 'other';

const SectionLabel: React.FC<{ kind: SectionKind; lang: 'ro' | 'en'; isExport?: boolean }> = ({
  kind,
  lang,
}) => {
  const isRo = lang === 'ro';
  const labels: Record<SectionKind, { ro: string; en: string }> = {
    freight: { ro: 'Navlu maritim', en: 'Sea freight' },
    thc: { ro: 'THC & taxe locale', en: 'THC & local charges' },
    land: { ro: 'Transport terestru', en: 'Inland transport' },
    other: { ro: 'Altele', en: 'Other' },
  };
  const icon =
    kind === 'freight' ? (
      <Ship size={11} />
    ) : kind === 'thc' ? (
      <Package size={11} />
    ) : kind === 'land' ? (
      <Truck size={11} />
    ) : (
      <Tag size={11} />
    );
  return (
    <span className={'offer-section-label offer-section-' + kind}>
      {icon}
      {isRo ? labels[kind].ro : labels[kind].en}
    </span>
  );
};

const SectionHeaderRow: React.FC<{
  kind: SectionKind;
  colSpan: number;
  lang: 'ro' | 'en';
  isExport?: boolean;
}> = ({ kind, colSpan, lang, isExport }) => (
  <tr className="offer-section-row">
    <td colSpan={colSpan}>
      <SectionLabel kind={kind} lang={lang} isExport={isExport} />
    </td>
  </tr>
);

// ---------- Desktop sections ----------

const FreightSection: React.FC<{
  rows: OfferCardFreightRow[];
  types: string[];
  lang: 'ro' | 'en';
  isExport: boolean;
  terminal?: string | null;
  currencyCode?: string | null;
  currencySymbol?: string | null;
  navluVat?: boolean;
}> = ({ rows, types, lang, isExport, terminal, currencyCode, currencySymbol, navluVat }) => (
  <>
    {/* Pe export nu afișăm bannerul de secțiune — doar matrix-ul prestație × prețuri. */}
    {!isExport && (
      <SectionHeaderRow kind="freight" colSpan={types.length + 1} lang={lang} isExport={isExport} />
    )}
    {rows.map((r) => {
      const label = freightServiceLabel(r);
      if (isExport) {
        return (
          <tr key={r.id} className="offer-row">
            <td className="offer-cell-service">
              <div className="fw-600 fs-13" style={{ lineHeight: 1.35, maxWidth: 420 }}>
                {label}
              </div>
            </td>
            <td className="offer-cell-terminal">{terminal || '—'}</td>
            {types.map((_, idx) => {
              const cell = formatExportPrice(
                r.prices[idx],
                r.currency || currencyCode,
                !!(r.plusVat || navluVat),
                lang,
              );
              return (
                <td key={idx} className="offer-cell-price mono fw-600 fs-13">
                  {cell || '—'}
                </td>
              );
            })}
          </tr>
        );
      }
      return (
        <tr key={r.id} className="offer-row">
          <td className="offer-cell-service">
            <div className="offer-route-cell">
              <span className="fw-600 fs-13">{r.polName || r.pol || '—'}</span>
              <ArrowRight size={11} strokeWidth={2} />
              <span className="fw-600 fs-13">{r.podName || r.pod || '—'}</span>
            </div>
          </td>
          {types.map((_, idx) => (
            <td key={idx} className="offer-cell-price">
              <PriceCell
                price={r.prices[idx] ?? null}
                symbol={r.currencySymbol || currencySymbol || null}
                code={r.currency || currencyCode || null}
                plusVat={r.plusVat}
                lang={lang}
              />
            </td>
          ))}
        </tr>
      );
    })}
  </>
);

const LocaleSection: React.FC<{
  rows: OfferCardLocaleRow[];
  types: string[];
  lang: 'ro' | 'en';
  isExport: boolean;
}> = ({ rows, types, lang, isExport }) => (
  <>
    <SectionHeaderRow kind="thc" colSpan={types.length + 1} lang={lang} isExport={isExport} />
    {rows.map((r) => (
      <tr key={r.id} className="offer-row">
        <td className="offer-cell-service">
          <div className="fs-12" style={{ lineHeight: 1.35, maxWidth: 420 }}>
            {r.name || '—'}
          </div>
        </td>
        {types.map((_, idx) => (
          <td key={idx} className="offer-cell-price">
            <PriceCell
              price={r.prices[idx] ?? null}
              symbol={r.currencySymbol}
              code={r.currency}
              plusVat={r.plusVat}
              lang={lang}
              emptyAsIncluded={isExport}
            />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const AutoSection: React.FC<{
  rows: OfferCardAutoRow[];
  types: string[];
  lang: 'ro' | 'en';
  isExport: boolean;
}> = ({ rows, types, lang, isExport }) => {
  const isRo = lang === 'ro';
  return (
    <>
      <SectionHeaderRow kind="land" colSpan={types.length + 1} lang={lang} isExport={isExport} />
      {rows.map((r) => (
        <tr key={r.id} className="offer-row offer-row-land">
          <td className="offer-cell-service">
            <div className="fs-12" style={{ lineHeight: 1.35, maxWidth: 420 }}>
              {r.name || '—'}
            </div>
          </td>
          <td className="offer-cell-price offer-cell-land" colSpan={Math.max(types.length, 1)}>
            {r.price != null ? (
              <>
                <span className="mono fw-600 fs-14">
                  {formatPrice(r.price, r.currencySymbol, r.currency)}
                </span>
                {r.plusVat && <span className="offer-vat">{isRo ? '+TVA' : '+VAT'}</span>}
              </>
            ) : isExport ? (
              <span className="muted fs-12" style={{ fontStyle: 'italic' }}>
                {isRo ? 'inclus / la cerere' : 'incl. / on request'}
              </span>
            ) : (
              <span className="offer-cell-empty">—</span>
            )}
          </td>
        </tr>
      ))}
    </>
  );
};

const OtherSection: React.FC<{
  rows: OfferCardOtherRow[];
  types: string[];
  lang: 'ro' | 'en';
  isExport: boolean;
}> = ({ rows, types, lang, isExport }) => (
  <>
    <SectionHeaderRow kind="other" colSpan={types.length + 1} lang={lang} isExport={isExport} />
    {rows.map((r) => (
      <tr key={r.id} className="offer-row">
        <td className="offer-cell-service">{r.name || '—'}</td>
        {types.map((_, idx) => (
          <td key={idx} className="offer-cell-price">
            <PriceCell
              price={r.prices[idx] ?? null}
              symbol={r.currencySymbol}
              code={r.currency}
              plusVat={r.plusVat}
              lang={lang}
              emptyAsIncluded={isExport}
            />
          </td>
        ))}
      </tr>
    ))}
  </>
);

// ---------- Mobile sections ----------

const FreightMobile: React.FC<{
  rows: OfferCardFreightRow[];
  types: string[];
  lang: 'ro' | 'en';
  isExport: boolean;
  terminal?: string | null;
  currencyCode?: string | null;
  currencySymbol?: string | null;
  navluVat?: boolean;
}> = ({ rows, types, lang, isExport, terminal, currencyCode, currencySymbol, navluVat }) => (
  <div className="offer-msec offer-msec-freight">
    {!isExport && <SectionLabel kind="freight" lang={lang} isExport={isExport} />}
    {rows.map((r) => (
      <div key={r.id} className="offer-mrow">
        <div className="offer-mrow-head">
          <span className="fw-600 fs-13">{freightServiceLabel(r)}</span>
        </div>
        {isExport && terminal ? (
          <div className="muted fs-11" style={{ marginTop: 2 }}>
            Terminal: {terminal}
          </div>
        ) : null}
        <div className="offer-mrow-prices">
          {types.map((tt, idx) => {
            if (isExport) {
              const cell = formatExportPrice(
                r.prices[idx],
                r.currency || currencyCode,
                !!(r.plusVat || navluVat),
                lang,
              );
              if (!cell) return null;
              return (
                <div key={tt} className="offer-mprice">
                  <span className="badge badge-neutral" style={{ fontSize: 10 }}>
                    {tt}
                  </span>
                  <span className="mono fw-600 fs-13">{cell}</span>
                </div>
              );
            }
            return r.prices[idx] != null ? (
              <div key={tt} className="offer-mprice">
                <span className="badge badge-neutral" style={{ fontSize: 10 }}>
                  {tt}
                </span>
                <span className="mono fw-600 fs-13">
                  {formatPrice(
                    r.prices[idx] as number,
                    r.currencySymbol || currencySymbol || null,
                    r.currency || currencyCode || null,
                  )}
                  {r.plusVat && (
                    <span className="offer-vat" style={{ marginLeft: 4 }}>
                      {lang === 'ro' ? '+TVA' : '+VAT'}
                    </span>
                  )}
                </span>
              </div>
            ) : null;
          })}
        </div>
      </div>
    ))}
  </div>
);

const LocaleMobile: React.FC<{
  rows: OfferCardLocaleRow[];
  types: string[];
  lang: 'ro' | 'en';
  isExport: boolean;
}> = ({ rows, types, lang, isExport }) => (
  <div className="offer-msec offer-msec-thc">
    <SectionLabel kind="thc" lang={lang} isExport={isExport} />
    {rows.map((r) => {
      const anyPrice = r.prices.some((p) => p != null);
      return (
        <div key={r.id} className="offer-mrow">
          <div className="offer-mrow-head fs-12">{r.name || '—'}</div>
          <div className="offer-mrow-prices">
            {!anyPrice && isExport ? (
              <span className="muted fs-12" style={{ fontStyle: 'italic' }}>
                {lang === 'ro' ? 'inclus' : 'incl.'}
              </span>
            ) : (
              types.map((tt, idx) =>
                r.prices[idx] != null ? (
                  <div key={tt} className="offer-mprice">
                    <span className="badge badge-neutral" style={{ fontSize: 10 }}>
                      {tt}
                    </span>
                    <span className="mono fw-600 fs-12">
                      {formatPrice(r.prices[idx] as number, r.currencySymbol, r.currency)}
                      {r.plusVat && (
                        <span className="offer-vat" style={{ marginLeft: 4 }}>
                          {lang === 'ro' ? '+TVA' : '+VAT'}
                        </span>
                      )}
                    </span>
                  </div>
                ) : null,
              )
            )}
          </div>
        </div>
      );
    })}
  </div>
);

const AutoMobile: React.FC<{ rows: OfferCardAutoRow[]; lang: 'ro' | 'en'; isExport: boolean }> = ({
  rows,
  lang,
  isExport,
}) => {
  const isRo = lang === 'ro';
  return (
    <div className="offer-msec offer-msec-land">
      <SectionLabel kind="land" lang={lang} isExport={isExport} />
      {rows.map((r) => (
        <div key={r.id} className="offer-mrow offer-mrow-land">
          <div className="offer-mrow-head fs-12">{r.name || '—'}</div>
          <div className="offer-mrow-prices">
            {r.price != null ? (
              <span className="mono fw-600 fs-13">
                {formatPrice(r.price, r.currencySymbol, r.currency)}
              </span>
            ) : isExport ? (
              <span className="muted fs-12" style={{ fontStyle: 'italic' }}>
                {isRo ? 'inclus / la cerere' : 'incl. / on request'}
              </span>
            ) : (
              <span className="offer-cell-empty">—</span>
            )}
            {r.price != null && r.plusVat && (
              <span className="offer-vat">{isRo ? '+TVA' : '+VAT'}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const OtherMobile: React.FC<{
  rows: OfferCardOtherRow[];
  types: string[];
  lang: 'ro' | 'en';
  isExport: boolean;
}> = ({ rows, types, lang, isExport }) => (
  <div className="offer-msec offer-msec-other">
    <SectionLabel kind="other" lang={lang} isExport={isExport} />
    {rows.map((r) => {
      const anyPrice = r.prices.some((p) => p != null);
      return (
        <div key={r.id} className="offer-mrow">
          <div className="offer-mrow-head fs-12">{r.name || '—'}</div>
          <div className="offer-mrow-prices">
            {!anyPrice && isExport ? (
              <span className="muted fs-12" style={{ fontStyle: 'italic' }}>
                {lang === 'ro' ? 'inclus' : 'incl.'}
              </span>
            ) : (
              types.map((tt, idx) =>
                r.prices[idx] != null ? (
                  <div key={tt} className="offer-mprice">
                    <span className="badge badge-neutral" style={{ fontSize: 10 }}>
                      {tt}
                    </span>
                    <span className="mono fw-600 fs-12">
                      {formatPrice(r.prices[idx] as number, r.currencySymbol, r.currency)}
                      {r.plusVat && (
                        <span className="offer-vat" style={{ marginLeft: 4 }}>
                          {lang === 'ro' ? '+TVA' : '+VAT'}
                        </span>
                      )}
                    </span>
                  </div>
                ) : null,
              )
            )}
          </div>
        </div>
      );
    })}
  </div>
);
